import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Container, Card, Row, Col, Button, Alert, Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faEdit, faFilePdf, faTrash, faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import { guaranteeService } from '../../services/api';
import LoadingSpinner from '../common/LoadingSpinner';
import ErrorMessage from '../common/ErrorMessage';
import PdfPreview from '../common/PdfPreview';
import { formatDate, getDateStatus, calculateGuaranteeDuration } from '../../utils/date-utils';

const GuaranteeDetail = () => {
  const { confirmationCode } = useParams();
  const navigate = useNavigate();
  
  const [guarantee, setGuarantee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [deleteInProgress, setDeleteInProgress] = useState(false);
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [pdfSuccess, setPdfSuccess] = useState(false);
  const [fetchedPrice, setFetchedPrice] = useState(null);
  const [priceLoading, setPriceLoading] = useState(false);
  
  useEffect(() => {
    const fetchGuarantee = async () => {
      try {
        setLoading(true);
        const data = await guaranteeService.getById(confirmationCode);
        setGuarantee(data);
        setError(null);
      } catch (err) {
        console.error('Error fetching guarantee details:', err);
        setError('Garantiedetails konnten nicht geladen werden. Bitte versuchen Sie es später erneut.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchGuarantee();
  }, [confirmationCode]);

  // Fetch price based on duration when guarantee is loaded
  useEffect(() => {
    const fetchPrice = async () => {
      if (!guarantee) return;
      
      try {
        setPriceLoading(true);
        const duration = calculateGuaranteeDuration(guarantee.issueDate, guarantee.expirationDate);
        
        if (duration) {
          const priceData = await guaranteeService.getPrices(duration);
          setFetchedPrice(priceData);
        }
      } catch (err) {
        console.error('Error fetching price:', err);
        // Keep fetchedPrice as null to fall back to stored price
      } finally {
        setPriceLoading(false);
      }
    };
    
    fetchPrice();
  }, [guarantee]);
  
  const handleDelete = async () => {
    try {
      setDeleteInProgress(true);
      setLoading(true);
      const result = await guaranteeService.delete(confirmationCode);
      
      if (result.success) {
        navigate('/guarantees', { 
          state: { message: 'Garantie erfolgreich gelöscht.' } 
        });
      } else {
        let errorMessage = 'Fehler beim Löschen der Garantie.';
        
        if (result.data && typeof result.data === 'object') {
          errorMessage = result.data.message || 
                        (result.data.status && result.data.status.includes(':') ? 
                         result.data.status.split(':')[1].trim() : 
                         errorMessage);
        } else if (result.message) {
          errorMessage = result.message;
        }
        
        setError(errorMessage);
        setShowDeleteModal(false);
      }
    } catch (err) {
      console.error('Error deleting guarantee:', err);
      setError('Garantie konnte nicht gelöscht werden. Bitte versuchen Sie es erneut.');
      setShowDeleteModal(false);
    } finally {
      setLoading(false);
      setDeleteInProgress(false);
    }
  };

  // Format price to German locale (EUR)
  const formatPrice = (price) => {
    return new Intl.NumberFormat('de-DE', { 
      style: 'currency', 
      currency: 'EUR' 
    }).format(price);
  };

  const handleGeneratePdf = async (template) => {
    try {
      setPdfGenerating(true);
      await guaranteeService.getPdf(confirmationCode, template);
      setPdfSuccess(true);
  
      setTimeout(() => {
        setShowPdfModal(false);
        setPdfSuccess(false);
      }, 1500);
    } catch (err) {
      console.error('Error generating PDF:', err);
      setError('PDF konnte nicht generiert werden. Bitte versuchen Sie es erneut.');
    } finally {
      setPdfGenerating(false);
    }
  };
  
  if (loading && !deleteInProgress) {
    return <LoadingSpinner />;
  }
  
  if (error && !guarantee && !deleteInProgress) {
    return (
      <Container>
        <ErrorMessage message={error}>
          <div className="mt-3">
            <Button variant="primary" as={Link} to="/guarantees">
              <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
              Zurück zu den Garantien
            </Button>
          </div>
        </ErrorMessage>
      </Container>
    );
  }
  
  if (!guarantee && !deleteInProgress) {
    return (
      <Container>
        <Alert variant="warning" className="my-4">
          Garantie nicht gefunden. Sie wurde möglicherweise gelöscht oder hat nie existiert.
          <div className="mt-3">
            <Button variant="primary" as={Link} to="/guarantees">
              <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
              Zurück zu den Garantien
            </Button>
          </div>
        </Alert>
      </Container>
    );
  }
  
  // Get expiration status for the guarantee
  const expirationStatus = guarantee ? getDateStatus(guarantee.expirationDate) : { isExpired: false };
  
  return (
    <Container>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Garantiedetails</h2>
        <div>
          <Button 
            variant="outline-primary" 
            as={Link} 
            to="/guarantees" 
            className="me-2"
          >
            <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
            Zurück zur Liste
          </Button>
          <Button 
            variant="outline-secondary" 
            as={Link} 
            to={`/guarantees/edit/${confirmationCode}`} 
            className="me-2"
          >
            <FontAwesomeIcon icon={faEdit} className="me-2" />
            Bearbeiten
          </Button>
          <Button 
            variant="outline-success" 
            onClick={() => setShowPdfModal(true)}
            className="me-2"
          >
            <FontAwesomeIcon icon={faFilePdf} className="me-2" />
            PDF generieren
          </Button>
          <Button 
            variant="outline-danger" 
            onClick={() => setShowDeleteModal(true)}
          >
            <FontAwesomeIcon icon={faTrash} className="me-2" />
            Löschen
          </Button>
        </div>
      </div>
      
      {error && <ErrorMessage message={error} onDismiss={() => setError(null)} />}
      
      {loading && deleteInProgress ? (
        <LoadingSpinner fullPage={false} />
      ) : (
        <>
          <Card className="mb-4">
            <Card.Header>
              <h5 className="mb-0">Garantieinformationen</h5>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={3} className="fw-bold">Bestätigungscode:</Col>
                <Col md={9}>{guarantee.confirmationCode}</Col>
              </Row>
              <hr />
              <Row>
                <Col md={3} className="fw-bold">Ausstellungsdatum:</Col>
                <Col md={9}>{formatDate(guarantee.issueDate)}</Col>
              </Row>
              <hr />
              <Row>
                <Col md={3} className="fw-bold">Ablaufdatum:</Col>
                <Col md={9}>
                  {formatDate(guarantee.expirationDate)}
                  {expirationStatus.isExpired && (
                    <span className={`badge ${expirationStatus.badgeClass} ms-2`}>
                      {expirationStatus.badgeText}
                    </span>
                  )}
                </Col>
              </Row>
              <hr />
              <Row>
                <Col md={3} className="fw-bold">Preis:</Col>
                <Col md={9}>
                  {priceLoading ? (
                    <span className="text-muted">Preis wird geladen...</span>
                  ) : (
                    formatPrice(fetchedPrice || guarantee.price)
                  )}
                </Col>
              </Row>
            </Card.Body>
          </Card>
          
          <Row className="mb-4">
            <Col md={6}>
              <Card className="h-100">
                <Card.Header>
                  <h5 className="mb-0">Kundeninformationen</h5>
                </Card.Header>
                <Card.Body>
                  <Row className="mb-2">
                    <Col sm={4} className="fw-bold">Name:</Col>
                    <Col sm={8}>
                      {guarantee.customer.firstName} {guarantee.customer.lastName}
                    </Col>
                  </Row>
                  {guarantee.customer.company && (
                    <Row className="mb-2">
                      <Col sm={4} className="fw-bold">Firma:</Col>
                      <Col sm={8}>{guarantee.customer.company}</Col>
                    </Row>
                  )}
                  <Row className="mb-2">
                    <Col sm={4} className="fw-bold">Adresse:</Col>
                    <Col sm={8}>
                      {guarantee.customer.street}<br />
                      {guarantee.customer.postalCode} {guarantee.customer.city}
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
            
            <Col md={6}>
              <Card className="h-100">
                <Card.Header>
                  <h5 className="mb-0">Fahrzeuginformationen</h5>
                </Card.Header>
                <Card.Body>
                  <Row className="mb-2">
                    <Col sm={4} className="fw-bold">Kennzeichen:</Col>
                    <Col sm={8}>{guarantee.vehicle.licensePlate}</Col>
                  </Row>
                  {guarantee.vehicle.vin && (
                    <Row className="mb-2">
                      <Col sm={4} className="fw-bold">VIN:</Col>
                      <Col sm={8}>{guarantee.vehicle.vin}</Col>
                    </Row>
                  )}
                  <Row className="mb-2">
                    <Col sm={4} className="fw-bold">Laufzeit:</Col>
                    <Col sm={8}>{guarantee.vehicle.duration} Tage</Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>
          
          <Card className="mb-4">
            <Card.Header>
              <h5 className="mb-0">Werkstattinformationen</h5>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <Row className="mb-2">
                    <Col sm={4} className="fw-bold">Kunden-ID:</Col>
                    <Col sm={8}>{guarantee.workshop.accountId}</Col>
                  </Row>
                  <Row className="mb-2">
                    <Col sm={4} className="fw-bold">System:</Col>
                    <Col sm={8}>{guarantee.workshop.system}</Col>
                  </Row>
                  <Row className="mb-2">
                    <Col sm={4} className="fw-bold">Firma:</Col>
                    <Col sm={8}>{guarantee.workshop.company}</Col>
                  </Row>
                </Col>
                <Col md={6}>
                  <Row className="mb-2">
                    <Col sm={4} className="fw-bold">Adresse:</Col>
                    <Col sm={8}>
                      {guarantee.workshop.street}<br />
                      {guarantee.workshop.postalCode} {guarantee.workshop.city}
                    </Col>
                  </Row>
                  <Row className="mb-2">
                    <Col sm={4} className="fw-bold">Kontakt:</Col>
                    <Col sm={8}>
                      {guarantee.workshop.email && (
                        <div>{guarantee.workshop.email}</div>
                      )}
                      {guarantee.workshop.phone1 && (
                        <div>{guarantee.workshop.phone1}</div>
                      )}
                      {guarantee.workshop.phone2 && (
                        <div>{guarantee.workshop.phone2}</div>
                      )}
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </>
      )}
      
      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Löschen bestätigen</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Sind Sie sicher, dass Sie diese Mobilitätsgarantie löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Abbrechen
          </Button>
          <Button variant="danger" onClick={handleDelete} disabled={deleteInProgress}>
            {deleteInProgress ? 'Wird gelöscht...' : 'Löschen'}
          </Button>
        </Modal.Footer>
      </Modal>
      
      {/* PDF Preview Modal */}
      <PdfPreview
        show={showPdfModal}
        onHide={() => setShowPdfModal(false)}
        onSelect={handleGeneratePdf}
        loading={pdfGenerating}
        success={pdfSuccess}
      />
    </Container>
  );
};

export default GuaranteeDetail;